import {Image} from '@shopify/hydrogen';
import {useCallback} from 'react';
import {useErrorLogger} from '@shopify/react-bugsnag';
import {usePageContext} from '../../contexts/PageContext.client';
import {MonorailEvent, timedTrackEvent} from '../../utils/monorail.client';
import {isMobile} from '../../utils/isMobile';
import {isShopifyLink, validateProtocol} from '../../utils/links';
import {LinkType} from 'shared/types';
import {
  buildCSS,
  CardStyle,
  LinkCardShape,
} from '../../../shared/utils/CardStyleUtil';
import classNames from 'classnames';

export default function BasicLink({
  id,
  title,
  img = '',
  url,
  style,
}: {
  id: string;
  title: string;
  url: string;
  img?: string;
  style: CardStyle;
}) {
  const pageCtx = usePageContext();
  const logger = useErrorLogger();

  const handleClick = useCallback(
    async (event: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
      event.preventDefault();
      event.stopPropagation();

      try {
        await timedTrackEvent({
          event: MonorailEvent.ClickLink,
          data: {
            linkId: id,
            linkTitle: title,
            pageId: pageCtx.id,
            linkUrl: url,
            linkType: LinkType.ExternalLink,
          },
        });
      } catch (error) {
        logger.notify(
          new Error(
            `event tracking error: ${MonorailEvent.ClickLink}: ${error}`,
          ),
        );
      }

      if (isMobile()) {
        window.location.href = url;
      } else {
        window.open(
          url,
          '_blank',
          'toolbar=1,menubar=1,location=1,status=1,scrollbars=1,noopener',
        );
      }
    },
    [id, title, url, pageCtx, logger],
  );

  const isRoundedFully = style.linkCardShape == LinkCardShape.RoundedLarge;

  return (
    <a
      className={classNames('w-full', 'text-black')}
      href={validateProtocol(url) ? url : '#'}
      target="_blank"
      rel={`noreferrer noopener ${isShopifyLink(url) ? '' : 'nofollow'}`}
      onClick={handleClick}
    >
      <div
        data-testid="link-card-container"
        className={classNames(
          'relative',
          'flex',
          'min-h-[60px]',
          'w-full',
          'items-center',
          'rounded',
          'bg-white',
          'shadow',
          'overflow-hidden',
          'border-[6px]',
          {
            'pr-[3rem]': img,
          },
        )}
        style={{...buildCSS(style), borderColor: style.linkCardColor}}
      >
        {img ? (
          <Image
            className={classNames(
              {
                'rounded-full	': isRoundedFully,
              },
              'h-12',
              'w-12',
              'object-cover',
            )}
            width={45}
            height={45}
            src={`${img}?width=192&height=192`}
            alt={title}
          />
        ) : null}
        <p
          className={classNames(
            'text-sm',
            'text-center',
            'w-full',
            'py-2.5',
            'mx-2',
          )}
        >
          {title}
        </p>
      </div>
    </a>
  );
}
